import { useCallback, useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useRecoilCallback } from 'recoil';
import {
	TourNotification,
	atom_tourNotifications,
	atom_activeMessageNotification,
	atom_messageNotificationQueue,
	atom_activeCTANotification,
	atom_ctaNotificationQueue,
	atom_triggeredNotificationIds,
} from '@app/shared/state/notification.atom';
import { atom_tourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { atom_modalType, atom_showModalContent } from '@app/shared/state/show-content-modal.atom';
import { service_tourService } from '@app/shared/services/tours.service';
import { queueNotificationUpdate, syncAllPendingUpdates, hasPendingUpdates } from '@app/shared/services/notification-offline.service';

type TriggerType = 'mapEntry' | 'location' | 'locationAndAction' | 'action';

interface TriggerContext {
	stopId?: string;
	location?: { lat: number; lng: number };
	actionId?: string;
}

/**
 * Mailman hook - manages notification triggers and display
 * Separates message (toast) and CTA (list/modal) notifications
 *
 * Usage:
 * - Map component: fireTrigger('mapEntry')
 * - Geolocation: fireTrigger('location', { location, stopId })
 * - Stop click: fireTrigger('action', { stopId })
 */
export type NotificationAction = 'seen' | 'dismissed' | 'ctaClicked' | 'ctaSkipped' | 'seenOnList';

export function useNotificationTrigger() {
	const [notifications, setNotifications] = useRecoilState(atom_tourNotifications);
	const tourState = useRecoilValue(atom_tourState);
	const syncAttemptedRef = useRef(false);

	// Sync pending notification updates when coming back online
	useEffect(() => {
		const syncPendingOnline = async () => {
			if (!hasPendingUpdates()) return;
			try {
				const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
				if (uID) {
					await syncAllPendingUpdates(uID);
				}
			} catch (err) {
				// Silently fail - will retry on next online event
			}
		};
		if (navigator.onLine && !syncAttemptedRef.current) {
			syncAttemptedRef.current = true;
			syncPendingOnline();
		}
		const handleOnline = () => {
			syncPendingOnline();
		};
		window.addEventListener('online', handleOnline);
		return () => window.removeEventListener('online', handleOnline);
	}, []);

	// Message notifications (Toast) - read-only for return values
	const activeMessage = useRecoilValue(atom_activeMessageNotification);
	const messageQueue = useRecoilValue(atom_messageNotificationQueue);

	// CTA notifications (List/Modal) - read-only for return values
	const activeCTA = useRecoilValue(atom_activeCTANotification);
	const ctaQueue = useRecoilValue(atom_ctaNotificationQueue);

	// Fire trigger - components call this
	// Uses useRecoilCallback to read fresh state from snapshot
	const fireTrigger = useRecoilCallback(({ snapshot, set }) => (
		triggerType: TriggerType,
		context?: TriggerContext
	) => {
		// For locationAndAction, auto-read geolocation if not provided
		if (triggerType === 'locationAndAction' && !context?.location) {
			const geoLoadable = snapshot.getLoadable(atom_geolocationState);
			const geo = geoLoadable.state === 'hasValue' ? geoLoadable.contents : null;
			if (geo && geo.latitude !== null && geo.longitude !== null) {
				context = { ...context, location: { lat: geo.latitude, lng: geo.longitude } };
			}
		}

		// Only trigger notifications when on the map stage
		const tourStageLoadable = snapshot.getLoadable(atom_tourStage);
		const tourStage = tourStageLoadable.state === 'hasValue' ? tourStageLoadable.contents : null;
		if (tourStage !== 'map') {
			return;
		}

		// Read fresh state from snapshot (with safety check)
		const notificationsLoadable = snapshot.getLoadable(atom_tourNotifications);
		const notificationsRaw = notificationsLoadable.state === 'hasValue' ? notificationsLoadable.contents : [];
		const notifications = Array.isArray(notificationsRaw) ? notificationsRaw : [];

		const triggeredIdsLoadable = snapshot.getLoadable(atom_triggeredNotificationIds);
		const triggeredIds = triggeredIdsLoadable.state === 'hasValue' ? triggeredIdsLoadable.contents : new Set<string>();

		const isDesktopLoadable = snapshot.getLoadable(atom_isDesktop);
		const isDesktop = isDesktopLoadable.state === 'hasValue' ? isDesktopLoadable.contents : false;

		// Find matching notifications
		const matched = notifications.filter((notif) => {
			// Skip inactive
			if (!notif.active) return false;

			// Skip displayOnce notifications already triggered in this session
			if (notif.displayOnce && triggeredIds.has(notif.id)) return false;

			// Skip displayOnce notifications that have already been seen (from backend)
			if (notif.displayOnce && notif.userState?.seen) return false;

			// Match trigger type
			if (notif.triggerType !== triggerType) return false;

			// For location-based triggers, check if user is within radius of the trigger location
			if (triggerType === 'location' || triggerType === 'locationAndAction') {
				if (!context?.location) return false;

				const triggerLocation = notif.triggerConfig?.location;
				const triggerRadius = notif.triggerConfig?.radius || 50; // Default 50 meters

				if (triggerLocation) {
					const distance = calculateDistance(
						context.location,
						{ lat: triggerLocation.latitude, lng: triggerLocation.longitude }
					);
					if (distance > triggerRadius) return false;
				}
			}

			// For action-based triggers, check if the action matches
			if (triggerType === 'action' || triggerType === 'locationAndAction') {
				if (notif.triggerConfig?.stopId && context?.stopId) {
					if (notif.triggerConfig.stopId !== context.stopId) return false;
				}
			}

			return true;
		}).sort((a, b) => a.order - b.order);

		if (matched.length === 0) {
			return;
		}

		// Mark all as triggered (including delayed ones, to prevent re-triggering)
		const newTriggeredIds = new Set(triggeredIds);
		matched.forEach((n) => newTriggeredIds.add(n.id));
		set(atom_triggeredNotificationIds, newTriggeredIds);

		// Split by notification type
		const allMessages = matched.filter(n => n.notificationType === 'message');
		const ctas = matched.filter(n => n.notificationType === 'cta'); // CTAs are always immediate, no delay

		// Split messages by delay - only messages support delays
		const messages = allMessages.filter(n => !n.triggerConfig?.delay || n.triggerConfig.delay === 0);
		const delayedMessages = allMessages.filter(n => n.triggerConfig?.delay && n.triggerConfig.delay > 0);

		// Handle immediate messages - show first, queue rest
		if (messages.length > 0) {
			const currentActiveLoadable = snapshot.getLoadable(atom_activeMessageNotification);
			const currentActive = currentActiveLoadable.state === 'hasValue' ? currentActiveLoadable.contents : null;

			if (!currentActive) {
				// No active message - set first as active, queue the rest
				set(atom_activeMessageNotification, messages[0]);

				if (messages.length > 1) {
					const toQueue = messages.slice(1);
					set(atom_messageNotificationQueue, (currentQueue) => [...currentQueue, ...toQueue]);
				}
			} else {
				// Active message exists - queue all
				set(atom_messageNotificationQueue, (currentQueue) => [...currentQueue, ...messages]);
			}
		}

		// Handle immediate CTAs - show first, queue rest
		if (ctas.length > 0) {
			const currentActiveCTALoadable = snapshot.getLoadable(atom_activeCTANotification);
			const currentActiveCTA = currentActiveCTALoadable.state === 'hasValue' ? currentActiveCTALoadable.contents : null;

			if (!currentActiveCTA) {
				// No active CTA - set first as active, queue the rest
				set(atom_activeCTANotification, ctas[0]);

				// On desktop, show CTA in modal
				if (isDesktop) {
					set(atom_modalType, 'desktopCTA');
					set(atom_showModalContent, true);
				}

				if (ctas.length > 1) {
					const toQueue = ctas.slice(1);
					set(atom_ctaNotificationQueue, (currentQueue) => [...currentQueue, ...toQueue]);
				}
			} else {
				// Active CTA exists - queue all
				set(atom_ctaNotificationQueue, (currentQueue) => [...currentQueue, ...ctas]);
			}
		}

		// Handle delayed messages - schedule them with setTimeout (CTAs don't support delays)
		for (const notif of delayedMessages) {
			const delayMs = (notif.triggerConfig?.delay || 0) * 1000;

			setTimeout(() => {
				// Try to set as active, or queue if something is already active
				let addedToActive = false;
				set(atom_activeMessageNotification, (current) => {
					if (!current) {
						addedToActive = true;
						return notif;
					}
					return current;
				});

				if (!addedToActive) {
					set(atom_messageNotificationQueue, (queue) => [...queue, notif]);
				}
			}, delayMs);
		}
	}, []);

	// Dismiss message and show next (same approach as dismissCTA)
	const dismissMessage = useRecoilCallback(({ snapshot, set }) => () => {
		const queueLoadable = snapshot.getLoadable(atom_messageNotificationQueue);
		const currentQueue = queueLoadable.state === 'hasValue' ? queueLoadable.contents : [];

		if (currentQueue.length > 0) {
			set(atom_activeMessageNotification, currentQueue[0]);
			set(atom_messageNotificationQueue, currentQueue.slice(1));
		} else {
			set(atom_activeMessageNotification, null);
		}
	}, []);

	// Dismiss CTA and show next
	const dismissCTA = useRecoilCallback(({ snapshot, set }) => () => {
		const queueLoadable = snapshot.getLoadable(atom_ctaNotificationQueue);
		const currentQueue = queueLoadable.state === 'hasValue' ? queueLoadable.contents : [];
		const desktopLoadable = snapshot.getLoadable(atom_isDesktop);
		const desktop = desktopLoadable.state === 'hasValue' ? desktopLoadable.contents : false;

		if (currentQueue.length > 0) {
			set(atom_activeCTANotification, currentQueue[0]);
			set(atom_ctaNotificationQueue, currentQueue.slice(1));
		} else {
			set(atom_activeCTANotification, null);
			if (desktop) {
				// Close the host modal the same way every other modal closes — clear both atoms.
				// Otherwise showModal stays true with no modalType, leaving an empty shell open.
				set(atom_modalType, null);
				set(atom_showModalContent, false);
			}
		}
	}, []);

	// Mark as seen - sync to backend and update local state (with offline support)
	const markAsSeen = useCallback((
		notificationId: string,
		action: NotificationAction = 'seen'
	) => {
		try {
			const tourId = tourState?.id;
			if (!tourId) {
				return;
			}

			let uID: string | undefined;
			try {
				const credentials = JSON.parse(localStorage.getItem('credential_items') || '{}');
				uID = credentials?.uID;
			} catch {
				return;
			}
			if (!uID) {
				return;
			}

			// Update local state immediately for responsive UI
			if (action === 'seen') {
				setNotifications((prev) => {
					if (!prev || !Array.isArray(prev)) return prev;
					return prev.map((notif) =>
						notif.id === notificationId
							? { ...notif, userState: { ...notif.userState, seen: true } }
							: notif
					);
				});
			}

			// Sync to backend if online, otherwise queue for later
			if (navigator.onLine) {
				service_tourService.updateNotificationState(tourId, notificationId, uID, action)
					.then((success) => {
						if (!success) {
							// API failed - queue for retry
							queueNotificationUpdate(tourId, notificationId, action);
						}
					})
					.catch(() => {
						// Network error - queue for retry
						queueNotificationUpdate(tourId, notificationId, action);
					});
			} else {
				// Offline - queue for sync when back online
				queueNotificationUpdate(tourId, notificationId, action);
			}
		} catch {
			// Silently fail - don't crash the app
		}
	}, [tourState?.id, setNotifications]);

	return {
		// Message notifications (for NotificationToast)
		activeMessage,
		dismissMessage,
		hasQueuedMessages: messageQueue.length > 0,

		// CTA notifications (for MobileCTAContent)
		activeCTA,
		dismissCTA,
		hasQueuedCTAs: ctaQueue.length > 0,

		// Shared
		fireTrigger,
		markAsSeen,
	};
}

// Haversine distance in meters
function calculateDistance(
	c1: { lat: number; lng: number },
	c2: { lat: number; lng: number }
): number {
	const R = 6371e3;
	const lat1 = (c1.lat * Math.PI) / 180;
	const lat2 = (c2.lat * Math.PI) / 180;
	const dLat = ((c2.lat - c1.lat) * Math.PI) / 180;
	const dLng = ((c2.lng - c1.lng) * Math.PI) / 180;

	const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
